import { useIsMobileView } from "@/contexts/ResponsiveProvider";
import { NativeSupportDetail } from "@/store/reducers/contents";
import ChevronRightIcon from "@/v2-components/core/icon/chevron-right-icon";
import CrossIcon from "@/v2-components/core/icon/cross-icon";
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/v2-components/ui/sheet";
import React from "react";
import { useTrans } from "@/utils/useTrans";

export default function HapticEffectSheet({
  effects,
  open,
  onOpenChange,
  highlight = [],
}: {
  effects: NativeSupportDetail[];
  /** 밖(햅틱 요약 카드의 줄)에서 열 때만 넘긴다 — 없으면 「전체 보기」 버튼이 여는 종전 동작 */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  /** 앞에 모아 강조할 카테고리 — 요약 줄이 말하는 이펙트(`KIND_EFFECT_CATEGORIES`) */
  highlight?: string[];
}) {
  const { t } = useTrans(["games"]);
  const isMobileVIew = useIsMobileView();
  const total = effects.reduce((acc, cur) => acc + cur.numberOfEffects, 0);
  // 강조할 카테고리를 앞으로 — 나머지는 원래 순서(어드민 순서) 그대로
  const lit = new Set(highlight);
  const ordered = [
    ...effects.filter((e) => lit.has(e.category)),
    ...effects.filter((e) => !lit.has(e.category)),
  ];

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      {/**
       * 시안 데스크탑 `29889:101431` · 모바일 `30035:21408` — **텍스트 링크 + chevron** 이고
       * 섹션 헤드 **우측**에 붙는다. 종전엔 그리드 아래 가운데의 어두운 pill
       * 버튼(`See N effects`)이었다.
       *
       * 문구는 기존 키 `See all` 을 쓴다 — 7 로케일에 이미 있어 새 키를 만들지 않는다.
       * 개수(`total`)는 시안 라벨에 없어서 뺐다.
       *
       * ★**글자 크기·색이 폭마다 다르다**(실측 2026-09-03):
       *
       * ```
       *          모바일 30035:21408        데스크탑 29889:101431
       *   글자    12/16 SemiBold tr .4     14/18 SemiBold tr .25
       *   색      #4d5358 (cg-70)          #21272a (cg-90)
       * ```
       *
       * ⚠️ **아이콘 크기는 시안에 없다 — 작성자 결정이다.** 데스크탑엔 24px 아이콘
       * 노드가 있지만 모바일 시안은 `전체 보기 ›` 처럼 **글리프가 문자열 안에** 있어서
       * 아이콘 노드가 없다. 12px 글자 옆에 24px 아이콘은 눈에 띄게 크므로 모바일에서
       * 줄 높이(16)에 가까운 16px 로 둔다. 문자열에 `›` 를 넣지 않는 이유는 그 방식이
       * 로케일마다 다른 문장부호를 강제하고 번역 키를 건드리기 때문이다.
       */}
      <SheetTrigger asChild>
        <button
          type="button"
          className="flex shrink-0 items-center text-sm font-600 tracking-[0.4px] text-coolgray-70 laptop:text-base laptop:tracking-[0.25px] laptop:text-coolgray-90"
        >
          {t("See all")}
          <ChevronRightIcon className="h-4 w-4 laptop:h-6 laptop:w-6" />
        </button>
      </SheetTrigger>
      <SheetContent
        className="flex flex-col laptop:min-w-[634px]"
        side={isMobileVIew ? "bottom" : "right"}
      >
        <SheetHeader className="items-center justify-between">
          <SheetTitle>{`Effects Category (${total})`}</SheetTitle>
          <SheetDescription className="sr-only">{`Effects Category`}</SheetDescription>
          <SheetClose className="focus:outline-none">
            <CrossIcon className="h-6 w-6" />
            <span className="sr-only">Close</span>
          </SheetClose>
        </SheetHeader>

        <div className="-mx-4 overflow-y-auto px-4 laptop:-mx-8 laptop:mt-5 laptop:px-8">
          {ordered.map((effect) => (
            <div
              className={
                lit.has(effect.category)
                  ? "-mx-4 border-b border-l-4 border-coolgray-20 border-l-blue-60 bg-blue-60/5 px-4 py-4 laptop:-mx-8 laptop:px-8 laptop:py-6"
                  : "border-b border-coolgray-20 py-4 last:border-b-0 laptop:py-6"
              }
              data-highlighted={lit.has(effect.category) || undefined}
              key={effect.category}
            >
              <h4 className="text-lg font-500">{effect.category}</h4>
              <p className="mt-2 text-base text-coolgray-70">{`${effect.numberOfEffects} different types embedded`}</p>
            </div>
          ))}
        </div>
      </SheetContent>
    </Sheet>
  );
}
